import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CAMERA_MAPPING_TARGETS,
  formatCameraOverlayApiError,
  previewCameraOverlayFile,
  type CameraFieldMapping,
  type CameraOverlayPreviewDto,
} from '../../services/cameraOverlayApi';
import {
  CAMERA_CATEGORY_COLORS,
  CAMERA_CATEGORY_LABELS,
  cameraCategoryLabel,
} from '../../services/cameraOverlayService';

export type CameraOverlayImportResult = {
  mapping: CameraFieldMapping;
  source: string;
  category: string;
  name: string;
};

type Props = {
  file: File | null;
  /** Position in a multi-file queue, for the title ("2 of 5"). */
  queue?: { index: number; total: number };
  busy?: boolean;
  onCancel: () => void;
  onSkip?: () => void;
  onImport: (result: CameraOverlayImportResult) => void;
};

const AUTO = '__auto__';
const NONE = '__none__';

function mappingForRequest(mapping: Record<string, string>): CameraFieldMapping {
  const out: CameraFieldMapping = {};
  CAMERA_MAPPING_TARGETS.forEach(({ key }) => {
    const v = mapping[key as string];
    if (v === undefined || v === AUTO) return;
    (out as Record<string, unknown>)[key as string] = v === NONE ? null : v;
  });
  return out;
}

/**
 * Preview + field mapper for a camera overlay import. The backend parses the
 * file (any supported format), suggests a mapping from the detected property
 * names, and re-parses whenever the admin changes a mapping so the counts,
 * category breakdown and sample rows always reflect what will be saved.
 */
const CameraOverlayImportDialog: React.FC<Props> = ({ file, queue, busy, onCancel, onSkip, onImport }) => {
  const [preview, setPreview] = useState<CameraOverlayPreviewDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [source, setSource] = useState('');
  const [category, setCategory] = useState('');
  const [name, setName] = useState('');
  const [keepAttributes, setKeepAttributes] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const reqRef = useRef(0);

  // Reset per file.
  useEffect(() => {
    setPreview(null);
    setError(null);
    setMapping({});
    setSource('');
    setCategory('');
    setName(file?.name ?? '');
    setKeepAttributes(true);
  }, [file]);

  const runPreview = useCallback(
    async (overrides: Record<string, string>, src: string, cat: string, keep: boolean) => {
      if (!file) return;
      const reqId = ++reqRef.current;
      setLoading(true);
      try {
        const req = mappingForRequest(overrides);
        req.keep_attributes = keep;
        const next = await previewCameraOverlayFile(file, {
          mapping: Object.keys(req).length ? req : null,
          source: src || null,
          category: cat || null,
        });
        if (reqId !== reqRef.current) return;
        setPreview(next);
        setError(null);
      } catch (e: unknown) {
        if (reqId !== reqRef.current) return;
        const msg = formatCameraOverlayApiError(e, 'Could not parse file');
        setError(msg);
        // Keep the last good preview so the admin can still fix the mapping;
        // a parse that yields no coordinates needs the field list, which we
        // may not have yet — try once more asking only for structure.
        if (!preview) setPreview(null);
      } finally {
        if (reqId === reqRef.current) setLoading(false);
      }
    },
    [file, preview]
  );

  // Initial preview when a file arrives.
  useEffect(() => {
    if (!file) return;
    void runPreview({}, '', '', true);
    // eslint-disable-next-line
  }, [file]);

  const fields = preview?.fields ?? [];
  const effective = preview?.mapping ?? {};

  const handleMappingChange = (key: string, value: string) => {
    const next = { ...mapping, [key]: value };
    setMapping(next);
    void runPreview(next, source, category, keepAttributes);
  };

  const handleBlurRefresh = () => {
    void runPreview(mapping, source, category, keepAttributes);
  };

  const categoryEntries = useMemo(
    () => Object.entries(preview?.category_counts ?? {}).sort((a, b) => b[1] - a[1]),
    [preview]
  );

  const sample = preview?.sample ?? [];
  const canImport = !!preview && preview.feature_count > 0 && !loading && !busy;

  const title = queue && queue.total > 1
    ? `Import camera file (${queue.index + 1} of ${queue.total})`
    : 'Import camera file';

  return (
    <Dialog open={!!file} onClose={busy ? undefined : onCancel} maxWidth="md" fullWidth>
      <DialogTitle>
        {title}
        {file && (
          <Typography variant="body2" color="text.secondary" component="div">
            {file.name} · {(file.size / 1024).toFixed(0)} KB
            {preview && (
              <>
                {' '}· detected <strong>{preview.source_format_label}</strong>
              </>
            )}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity={preview ? 'warning' : 'error'} sx={{ mb: 2 }}>
            {error}
            {!preview && (
              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                If this is a CSV or JSON table, the latitude / longitude columns may need to be mapped
                below once the file structure is known.
              </Typography>
            )}
          </Alert>
        )}
        {preview?.warnings?.map((w) => (
          <Alert key={w} severity="info" sx={{ mb: 1 }}>
            {w}
          </Alert>
        ))}

        {/* Summary */}
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 2 }}>
          {loading && <CircularProgress size={18} />}
          {preview && (
            <Chip
              size="small"
              color="primary"
              label={`${preview.feature_count} camera${preview.feature_count === 1 ? '' : 's'}`}
            />
          )}
          {preview && preview.skipped > 0 && (
            <Chip size="small" variant="outlined" label={`${preview.skipped} skipped`} />
          )}
          {categoryEntries.map(([slug, n]) => (
            <Chip
              key={slug}
              size="small"
              label={`${cameraCategoryLabel(slug)}: ${n}`}
              sx={{
                bgcolor: CAMERA_CATEGORY_COLORS[slug] || CAMERA_CATEGORY_COLORS.other,
                color: '#fff',
              }}
            />
          ))}
        </Stack>

        {/* File-level settings */}
        <Grid container spacing={2} sx={{ mb: 1 }}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Save as"
              value={name}
              onChange={(e) => setName(e.target.value)}
              helperText="Re-importing the same name replaces it"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Source"
              placeholder={preview?.source || 'e.g. OpenStreetMap, DDOT open data'}
              value={source}
              onChange={(e) => setSource(e.target.value)}
              onBlur={handleBlurRefresh}
              helperText="Default for rows without their own source"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="cam-import-category">Default category</InputLabel>
              <Select
                labelId="cam-import-category"
                label="Default category"
                value={category}
                onChange={(e) => {
                  setCategory(e.target.value);
                  void runPreview(mapping, source, e.target.value, keepAttributes);
                }}
              >
                <MenuItem value="">
                  <em>Infer from data</em>
                </MenuItem>
                {Object.entries(CAMERA_CATEGORY_LABELS).map(([slug, label]) => (
                  <MenuItem key={slug} value={slug}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Field mapping */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="subtitle2">Field mapping</Typography>
          <Typography variant="caption" color="text.secondary">
            {fields.length} field{fields.length === 1 ? '' : 's'} detected
            {preview?.source_format && ['geojson', 'overpass', 'osm', 'gpx', 'kml', 'kmz'].includes(preview.source_format)
              ? ' · coordinates come from the geometry'
              : ''}
          </Typography>
        </Stack>
        <Grid container spacing={1.5}>
          {CAMERA_MAPPING_TARGETS.map(({ key, label, hint }) => {
            const chosen = mapping[key as string] ?? AUTO;
            const auto = (effective as Record<string, string | null | undefined>)[key as string];
            return (
              <Grid item xs={12} sm={6} md={4} key={key as string}>
                <Tooltip title={hint} placement="top" arrow>
                  <FormControl fullWidth size="small" disabled={!preview && fields.length === 0}>
                    <InputLabel id={`cam-map-${key as string}`}>{label}</InputLabel>
                    <Select
                      labelId={`cam-map-${key as string}`}
                      label={label}
                      value={chosen}
                      onChange={(e) => handleMappingChange(key as string, e.target.value)}
                      renderValue={(v) =>
                        v === AUTO
                          ? `Auto${auto ? ` → ${auto}` : ' (none found)'}`
                          : v === NONE
                            ? 'Not mapped'
                            : String(v)
                      }
                    >
                      <MenuItem value={AUTO}>
                        <em>Auto{auto ? ` → ${auto}` : ' (none found)'}</em>
                      </MenuItem>
                      <MenuItem value={NONE}>
                        <em>Not mapped</em>
                      </MenuItem>
                      {fields.map((f) => (
                        <MenuItem key={f} value={f}>
                          {f}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Tooltip>
              </Grid>
            );
          })}
        </Grid>
        <FormControlLabel
          sx={{ mt: 1 }}
          control={
            <Switch
              size="small"
              checked={keepAttributes}
              onChange={(e) => {
                setKeepAttributes(e.target.checked);
                void runPreview(mapping, source, category, e.target.checked);
              }}
            />
          }
          label={<Typography variant="body2">Keep unmapped fields as attributes (shown in the popup)</Typography>}
        />

        {/* Sample */}
        {sample.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="subtitle2">Sample ({Math.min(sample.length, showAll ? 8 : 4)} of {preview?.feature_count})</Typography>
              {sample.length > 4 && (
                <Button size="small" onClick={() => setShowAll((v) => !v)}>
                  {showAll ? 'Show fewer' : 'Show more'}
                </Button>
              )}
            </Stack>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell>Lat, Lon</TableCell>
                    <TableCell>Attributes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sample.slice(0, showAll ? 8 : 4).map((f, i) => {
                    const p = f.properties || {};
                    const attrs = (p.attributes && typeof p.attributes === 'object'
                      ? Object.keys(p.attributes as Record<string, unknown>)
                      : []);
                    return (
                      <TableRow key={`${String(p.camera_id)}-${i}`}>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{String(p.camera_id ?? '')}</TableCell>
                        <TableCell>{String(p.camera_name ?? '')}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={cameraCategoryLabel(p.category as string)}
                            sx={{
                              bgcolor: CAMERA_CATEGORY_COLORS[String(p.category)] || CAMERA_CATEGORY_COLORS.other,
                              color: '#fff',
                              height: 20,
                            }}
                          />
                        </TableCell>
                        <TableCell>{p.status ? String(p.status) : '—'}</TableCell>
                        <TableCell>{p.source ? String(p.source) : '—'}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'nowrap' }}>
                          {Number(p.latitude).toFixed(5)}, {Number(p.longitude).toFixed(5)}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 220 }}>
                          <Typography variant="caption" color="text.secondary" noWrap title={attrs.join(', ')}>
                            {attrs.length ? attrs.join(', ') : '—'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>
          Cancel{queue && queue.total > 1 ? ' all' : ''}
        </Button>
        {onSkip && queue && queue.total > 1 && (
          <Button onClick={onSkip} disabled={busy}>
            Skip this file
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          disabled={!canImport}
          onClick={() => {
            const req = mappingForRequest(mapping);
            req.keep_attributes = keepAttributes;
            onImport({ mapping: req, source, category, name: name.trim() || (file?.name ?? '') });
          }}
        >
          {busy ? 'Importing…' : `Import${preview ? ` ${preview.feature_count} cameras` : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CameraOverlayImportDialog;
